'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, Share2, MapPin, Eye, Clock, Star, Shield, Phone, MessageCircle, ChevronLeft, ChevronRight, X, Check, Copy, Bookmark, BookmarkCheck, Send, BadgeCheck, Tag, Briefcase, Hotel, Home, Car, Package, Pill, Utensils, Truck, Calendar, Video, Zap, Building2 } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// ─── Static data maps per module ─────────────────────────────────────────────
const MODULE_DATA: Record<string, { label: string; icon: React.ElementType; color: string; href: string }> = {
  classifieds: { label: 'Classifieds', icon: Tag, color: '#FFB300', href: '/classifieds' },
  jobs: { label: 'Jobs', icon: Briefcase, color: '#00BCD4', href: '/jobs' },
  hotels: { label: 'Hotels', icon: Hotel, color: '#FF6D00', href: '/hotels' },
  property: { label: 'Property', icon: Home, color: '#7C4DFF', href: '/property' },
  vehicles: { label: 'Vehicles', icon: Car, color: '#FF4081', href: '/vehicles' },
  pharmacy: { label: 'Pharmacy', icon: Pill, color: '#00C853', href: '/pharmacy' },
  food: { label: 'Food', icon: Utensils, color: '#FF6D00', href: '/food' },
  transport: { label: 'Transport', icon: Truck, color: '#26C6DA', href: '/transport' },
  events: { label: 'Events', icon: Calendar, color: '#E91E63', href: '/events' },
  videos: { label: 'Videos', icon: Video, color: '#FF3B5C', href: '/videos' },
  services: { label: 'Services', icon: Zap, color: '#FFB300', href: '/services' },
  business: { label: 'Business', icon: Building2, color: '#7C4DFF', href: '/business' },
  marketplace: { label: 'Marketplace', icon: Package, color: '#00C853', href: '/marketplace' },
};

// ─── Mock listing data (fallback when DB row not found) ──────────────────────
const MOCK_LISTINGS: Record<string, any> = {
  classifieds: {
    id: 'demo', title: 'iPhone 15 Pro Max 256GB — Space Black', price: 145000, currency: 'KES',
    location: 'Nairobi CBD', category: 'Electronics', condition: 'Used', posted: '2 hours ago',
    views: 234, description: 'Excellent condition, no scratches. Comes with original box and charger. Battery health 97%. Face ID works perfectly. Selling because I upgraded to iPhone 16.',
    images: [
      'https://img.rocket.new/generatedImages/rocket_gen_img_153be61b7-1774419612914.png',
      'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=800',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800',
    ],
    seller: { name: 'Peter M', avatar: null, rating: 4.8, reviews: 23, member_since: 'Jan 2023', verified: true, phone: '+254712345678', location: 'Nairobi CBD' },
    tags: ['iPhone', 'Apple', 'Smartphone', '5G'],
    is_featured: true,
  },
  jobs: {
    id: 'demo', title: 'Senior Software Engineer', company: 'Safaricom PLC', salary: 'KES 150K–200K',
    location: 'Nairobi', type: 'Full-time', category: 'Technology', posted: '2 days ago',
    views: 567, description: 'Join our engineering team to build next-generation telecom solutions. You will work on scalable microservices, mobile APIs, and data pipelines serving 40M+ customers.',
    images: ['https://img.rocket.new/generatedImages/rocket_gen_img_1bf2e0883-1783837235616.png'],
    seller: { name: 'HR Team', avatar: null, rating: 4.9, reviews: 145, member_since: 'Mar 2020', verified: true, phone: '+254700000000', location: 'Nairobi' },
    tags: ['Software', 'Engineering', 'Full-time', 'Tech'],
    is_featured: true,
  },
  hotels: {
    id: 'demo', title: 'Serena Hotel Nairobi', price: 18500, currency: 'KES',
    location: 'Processional Way, Nairobi', category: 'Luxury', condition: '5 Stars', posted: 'Listed 2023',
    views: 1240, description: 'Iconic 5-star hotel in the heart of Nairobi with world-class amenities. Enjoy our award-winning restaurant, rooftop pool, and state-of-the-art conference facilities.',
    images: [
      'https://img.rocket.new/generatedImages/rocket_gen_img_197cc4286-1772473839089.png',
      'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
    ],
    seller: { name: 'Serena Hotels', avatar: null, rating: 4.8, reviews: 1240, member_since: 'Jan 2020', verified: true, phone: '+254202822000', location: 'Nairobi' },
    tags: ['Luxury', 'Pool', 'Restaurant', 'Conference'],
    is_featured: true,
  },
  property: {
    id: 'demo', title: '3-Bedroom Apartment — Westlands', price: 85000, currency: 'KES/mo',
    location: 'Westlands, Nairobi', category: 'Apartment', condition: 'For Rent', posted: '1 week ago',
    views: 389, description: 'Modern apartment in prime Westlands location. Fully furnished option available. 24/7 security, backup generator, and ample parking.',
    images: [
      'https://img.rocket.new/generatedImages/rocket_gen_img_1ee3d460c-1784530799137.png',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
    ],
    seller: { name: 'Peter M', avatar: null, rating: 4.7, reviews: 34, member_since: 'Jun 2021', verified: true, phone: '+254712345678', location: 'Westlands' },
    tags: ['3BR', 'Furnished', 'Westlands', 'Apartment'],
    is_featured: true,
  },
};

function getModuleMock(module: string) {
  return MOCK_LISTINGS[module] || MOCK_LISTINGS['classifieds'];
}

// ─── Image Gallery ────────────────────────────────────────────────────────────
function ImageGallery({ images, title }: { images: string[]; title: string }) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const prev = () => setCurrent(i => (i - 1 + images.length) % images.length);
  const next = () => setCurrent(i => (i + 1) % images.length);

  return (
    <>
      <div className="relative w-full overflow-hidden rounded-2xl" style={{ background: '#0d1117' }}>
        {/* Main image */}
        <div className="relative aspect-video cursor-pointer" onClick={() => setLightbox(true)}>
          <img
            src={images[current]}
            alt={`${title} — image ${current + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
          />
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all"
                style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <ChevronLeft size={18} className="text-white" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all"
                style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <ChevronRight size={18} className="text-white" />
              </button>
            </>
          )}
          <div className="absolute bottom-3 right-3 px-2 py-1 rounded-lg text-xs font-600"
            style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>
            {current + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto">
            {images.map((img, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className="flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden transition-all"
                style={{ border: i === current ? '2px solid #00C853' : '2px solid transparent', opacity: i === current ? 1 : 0.6 }}>
                <img src={img} alt={`thumb ${i + 1}`} className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={() => setLightbox(false)}>
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)' }}>
            <X size={20} className="text-white" />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.1)' }}>
                <ChevronLeft size={22} className="text-white" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.1)' }}>
                <ChevronRight size={22} className="text-white" />
              </button>
            </>
          )}
          <img src={images[current]} alt={title} className="max-w-full max-h-full object-contain rounded-xl"
            style={{ maxWidth: '90vw', maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }} />
        </div>
      )}
    </>
  );
}

// ─── Seller Profile Card ──────────────────────────────────────────────────────
function SellerCard({ seller, module }: { seller: any; module: string }) {
  const initials = seller?.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || 'S';
  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <h3 className="text-sm font-700 mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
        {module === 'jobs' ? 'EMPLOYER' : module === 'hotels' ? 'PROPERTY' : 'SELLER'} PROFILE
      </h3>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-800 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #00C853, #00BCD4)', color: '#fff' }}>
          {seller?.avatar ? (
            <img src={seller.avatar} alt={seller.name} className="w-full h-full rounded-full object-cover" />
          ) : initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-700 text-sm truncate" style={{ color: '#fff' }}>{seller?.name || 'Seller'}</span>
            {seller?.verified && <BadgeCheck size={15} style={{ color: '#00C853', flexShrink: 0 }} />}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <MapPin size={11} style={{ color: 'rgba(255,255,255,0.4)' }} />
            <span className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{seller?.location || 'Kenya'}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-xl p-2.5 text-center" style={{ background: 'rgba(0,200,83,0.08)' }}>
          <div className="text-base font-800" style={{ color: '#00C853' }}>{seller?.rating || '4.8'}</div>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Rating</div>
        </div>
        <div className="rounded-xl p-2.5 text-center" style={{ background: 'rgba(0,188,212,0.08)' }}>
          <div className="text-base font-800" style={{ color: '#00BCD4' }}>{seller?.reviews || '0'}</div>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Reviews</div>
        </div>
        <div className="rounded-xl p-2.5 text-center" style={{ background: 'rgba(124,77,255,0.08)' }}>
          <div className="text-xs font-700" style={{ color: '#7C4DFF' }}>{seller?.member_since || '2023'}</div>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Member</div>
        </div>
      </div>
      {seller?.phone && (
        <a href={`tel:${seller.phone}`}
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-700 transition-all"
          style={{ background: 'rgba(0,200,83,0.12)', color: '#00C853', border: '1px solid rgba(0,200,83,0.2)' }}>
          <Phone size={15} /> Call Seller
        </a>
      )}
    </div>
  );
}

// ─── Share Panel ──────────────────────────────────────────────────────────────
function SharePanel({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : url;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const shareOptions = [
    { label: 'WhatsApp', color: '#25D366', icon: '💬', href: `https://wa.me/?text=${encodeURIComponent(title + ' ' + shareUrl)}` },
    { label: 'Facebook', color: '#1877F2', icon: '📘', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { label: 'Twitter/X', color: '#1DA1F2', icon: '🐦', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}` },
    { label: 'LinkedIn', color: '#0A66C2', icon: '💼', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="w-full max-w-lg rounded-t-3xl p-6 pb-8" style={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.2)' }} />
        <h3 className="text-base font-700 mb-4 text-white">Share this listing</h3>
        <div className="grid grid-cols-4 gap-3 mb-5">
          {shareOptions.map((opt) => (
            <a key={opt.label} href={opt.href} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-3 rounded-2xl transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="text-2xl">{opt.icon}</span>
              <span className="text-xs font-600" style={{ color: 'rgba(255,255,255,0.6)' }}>{opt.label}</span>
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2 p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <span className="flex-1 text-xs truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{shareUrl}</span>
          <button onClick={copyLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-700 transition-all"
            style={{ background: copied ? '#00C853' : 'rgba(0,200,83,0.15)', color: copied ? '#fff' : '#00C853' }}>
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <button onClick={onClose} className="w-full py-3 rounded-xl text-sm font-700"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Inquiry Modal ────────────────────────────────────────────────────────────
function InquiryModal({ listing, module, sellerId, onClose, onSent }: {
  listing: any; module: string; sellerId: string; onClose: () => void; onSent: () => void;
}) {
  const { user } = useAuth();
  const supabase = createClient();
  const [message, setMessage] = useState(`Hi, I'm interested in "${listing?.title}". Is it still available?`);
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    if (!user) { setError('Please sign in to send an inquiry.'); return; }
    if (!message.trim()) { setError('Please enter a message.'); return; }
    setSending(true);
    setError('');
    try {
      const { error: err } = await supabase.from('listing_inquiries').insert({
        listing_id: listing?.id || 'unknown',
        module,
        sender_id: user.id,
        seller_id: sellerId,
        message: message.trim(),
        phone: phone.trim() || null,
        email: user.email || null,
      });
      if (err) throw err;
      onSent();
    } catch (e: any) {
      setError(e?.message || 'Failed to send inquiry. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="w-full max-w-lg rounded-t-3xl p-6 pb-8" style={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.2)' }} />
        <h3 className="text-base font-700 mb-1 text-white">Send Inquiry</h3>
        <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>Your message will be sent to the seller</p>
        {error && (
          <div className="mb-4 p-3 rounded-xl text-sm" style={{ background: 'rgba(255,59,92,0.12)', color: '#FF3B5C', border: '1px solid rgba(255,59,92,0.2)' }}>
            {error}
          </div>
        )}
        <textarea
          value={message} onChange={(e) => setMessage(e.target.value)}
          rows={4} placeholder="Your message..."
          className="w-full rounded-xl p-3 text-sm resize-none mb-3 outline-none"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
        <input
          value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="Your phone number (optional)"
          className="w-full rounded-xl p-3 text-sm mb-4 outline-none"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl text-sm font-700"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </button>
          <button onClick={send} disabled={sending}
            className="flex-1 py-3 rounded-xl text-sm font-700 flex items-center justify-center gap-2 transition-all"
            style={{ background: sending ? 'rgba(0,200,83,0.4)' : '#00C853', color: '#fff' }}>
            {sending ? 'Sending...' : <><Send size={14} /> Send Message</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ListingDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();

  const module = (params?.module as string) || 'classifieds';
  const id = (params?.id as string) || 'demo';

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);
  const viewTracked = useRef(false);

  const moduleInfo = MODULE_DATA[module] || MODULE_DATA['classifieds'];

  // Load listing from seller_listings table or fall back to mock
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from('seller_listings')
          .select('*, seller:seller_id(id, full_name, avatar_url, location)')
          .eq('id', id)
          .eq('module', module)
          .maybeSingle();

        if (data) {
          const images = Array.isArray(data.images) ? data.images : [];
          setListing({
            id: data.id,
            title: data.title,
            price: data.price,
            currency: data.currency || 'KES',
            location: data.location,
            category: data.category,
            condition: data.metadata?.condition || '',
            posted: new Date(data.created_at).toLocaleDateString(),
            views: data.views || 0,
            description: data.description,
            images: images.length > 0 ? images : ['/assets/images/no_image.png'],
            seller: {
              id: data.seller?.id,
              name: data.seller?.full_name || 'Seller',
              avatar: data.seller?.avatar_url || null,
              rating: 4.8,
              reviews: 0,
              member_since: '2023',
              verified: true,
              phone: data.metadata?.phone || null,
              location: data.seller?.location || data.location,
            },
            tags: data.metadata?.tags || [],
            is_featured: data.status === 'active',
            sellerId: data.seller_id,
          });
        } else {
          // Use mock data
          const mock = getModuleMock(module);
          setListing({ ...mock, sellerId: 'mock-seller' });
        }
      } catch {
        const mock = getModuleMock(module);
        setListing({ ...mock, sellerId: 'mock-seller' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [module, id]);

  // Track view
  useEffect(() => {
    if (!listing || viewTracked.current) return;
    viewTracked.current = true;
    supabase.from('listing_views').insert({
      listing_id: id,
      module,
      viewer_id: user?.id || null,
    }).then(() => {});
  }, [listing]);

  // Check if saved
  useEffect(() => {
    if (!user || !listing) return;
    supabase.from('saved_listings')
      .select('id')
      .eq('user_id', user.id)
      .eq('module', module)
      .eq('listing_id', id)
      .maybeSingle()
      .then(({ data }) => setSaved(!!data));
  }, [user, listing]);

  const toggleSave = useCallback(async () => {
    if (!user) { router.push('/sign-up-login-screen'); return; }
    setSaveLoading(true);
    try {
      if (saved) {
        await supabase.from('saved_listings')
          .delete()
          .eq('user_id', user.id)
          .eq('module', module)
          .eq('listing_id', id);
        setSaved(false);
      } else {
        await supabase.from('saved_listings').insert({
          user_id: user.id,
          module,
          listing_id: id,
          listing_data: {
            title: listing?.title,
            price: listing?.price,
            image: listing?.images?.[0],
            location: listing?.location,
          },
        });
        setSaved(true);
      }
    } catch {}
    setSaveLoading(false);
  }, [user, saved, listing, module, id]);

  const formatPrice = (p: number, currency = 'KES') => {
    if (!p) return 'Contact for price';
    return `${currency} ${p.toLocaleString()}`;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: '#070C1A' }}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center animate-pulse"
              style={{ background: 'rgba(0,200,83,0.12)', border: '1px solid rgba(0,200,83,0.2)' }}>
              <span className="text-2xl">🔍</span>
            </div>
            <p className="text-sm font-600" style={{ color: 'rgba(255,255,255,0.5)' }}>Loading listing...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!listing) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: '#070C1A' }}>
          <div className="text-center">
            <p className="text-lg font-700 text-white mb-2">Listing not found</p>
            <Link href={moduleInfo.href} className="text-sm" style={{ color: '#00C853' }}>← Back to {moduleInfo.label}</Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  const sellerId = listing?.sellerId || listing?.seller?.id || 'unknown';

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-3"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={() => router.back()}
            className="flex items-center gap-2 text-sm font-600 transition-all"
            style={{ color: 'rgba(255,255,255,0.7)' }}>
            <ArrowLeft size={18} /> Back
          </button>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-600"
            style={{ background: `rgba(${moduleInfo.color === '#FFB300' ? '255,179,0' : '0,200,83'},0.12)`, color: moduleInfo.color, border: `1px solid ${moduleInfo.color}30` }}>
            <moduleInfo.icon size={11} /> {moduleInfo.label}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleSave} disabled={saveLoading}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
              style={{ background: saved ? 'rgba(255,64,129,0.15)' : 'rgba(255,255,255,0.06)', border: `1px solid ${saved ? 'rgba(255,64,129,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              {saved ? <BookmarkCheck size={17} style={{ color: '#FF4081' }} /> : <Bookmark size={17} style={{ color: 'rgba(255,255,255,0.6)' }} />}
            </button>
            <button onClick={() => setShowShare(true)}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <Share2 size={17} style={{ color: 'rgba(255,255,255,0.6)' }} />
            </button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-5 space-y-5">
          {/* Image Gallery */}
          <ImageGallery images={listing.images || ['/assets/images/no_image.png']} title={listing.title} />

          {/* Title & Price */}
          <div>
            {listing.is_featured && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-700 mb-2"
                style={{ background: 'rgba(255,179,0,0.12)', color: '#FFB300', border: '1px solid rgba(255,179,0,0.2)' }}>
                <Star size={11} fill="#FFB300" /> Featured
              </div>
            )}
            <h1 className="text-xl font-800 leading-tight mb-2" style={{ color: '#fff' }}>{listing.title}</h1>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-2xl font-900" style={{ color: '#00C853' }}>
                {formatPrice(listing.price, listing.currency)}
              </div>
              <div className="flex items-center gap-3 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                <span className="flex items-center gap-1"><Eye size={12} /> {listing.views || 0} views</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {listing.posted}</span>
              </div>
            </div>
          </div>

          {/* Meta chips */}
          <div className="flex flex-wrap gap-2">
            {listing.location && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <MapPin size={11} /> {listing.location}
              </span>
            )}
            {listing.category && (
              <span className="px-3 py-1.5 rounded-full text-xs font-600"
                style={{ background: 'rgba(0,188,212,0.1)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.2)' }}>
                {listing.category}
              </span>
            )}
            {listing.condition && (
              <span className="px-3 py-1.5 rounded-full text-xs font-600"
                style={{ background: 'rgba(124,77,255,0.1)', color: '#7C4DFF', border: '1px solid rgba(124,77,255,0.2)' }}>
                {listing.condition}
              </span>
            )}
          </div>

          {/* Description */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <h2 className="text-sm font-700 mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>DESCRIPTION</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>{listing.description}</p>
          </div>

          {/* Tags */}
          {listing.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {listing.tags.map((tag: string) => (
                <span key={tag} className="px-2.5 py-1 rounded-lg text-xs font-600"
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Seller Card */}
          <SellerCard seller={listing.seller} module={module} />

          {/* Safety notice */}
          <div className="flex items-start gap-3 p-4 rounded-2xl"
            style={{ background: 'rgba(0,200,83,0.05)', border: '1px solid rgba(0,200,83,0.12)' }}>
            <Shield size={16} style={{ color: '#00C853', flexShrink: 0, marginTop: 1 }} />
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Always meet in a safe public place. Never send money in advance. China Ndogo does not guarantee any transaction.
            </p>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3 pb-6">
            <button onClick={() => setShowInquiry(true)}
              className="flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-700 transition-all"
              style={{ background: 'rgba(0,200,83,0.12)', color: '#00C853', border: '1px solid rgba(0,200,83,0.25)' }}>
              <MessageCircle size={16} /> Send Inquiry
            </button>
            {listing.seller?.phone ? (
              <a href={`tel:${listing.seller.phone}`}
                className="flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-700 transition-all"
                style={{ background: '#00C853', color: '#fff' }}>
                <Phone size={16} /> Call Now
              </a>
            ) : (
              <button onClick={() => setShowShare(true)}
                className="flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-700 transition-all"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}>
                <Share2 size={16} /> Share
              </button>
            )}
          </div>
        </div>

        {/* Modals */}
        {showShare && (
          <SharePanel url={typeof window !== 'undefined' ? window.location.href : ''} title={listing.title} onClose={() => setShowShare(false)} />
        )}
        {showInquiry && !inquirySent && (
          <InquiryModal
            listing={listing} module={module} sellerId={sellerId}
            onClose={() => setShowInquiry(false)}
            onSent={() => { setInquirySent(true); setShowInquiry(false); }}
          />
        )}
        {inquirySent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}
            onClick={() => setInquirySent(false)}>
            <div className="rounded-3xl p-8 text-center mx-4" style={{ background: '#0d1117', border: '1px solid rgba(0,200,83,0.2)' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(0,200,83,0.12)' }}>
                <Check size={28} style={{ color: '#00C853' }} />
              </div>
              <h3 className="text-lg font-800 text-white mb-2">Inquiry Sent!</h3>
              <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.5)' }}>The seller will get back to you shortly.</p>
              <button onClick={() => setInquirySent(false)}
                className="px-6 py-2.5 rounded-xl text-sm font-700"
                style={{ background: '#00C853', color: '#fff' }}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
